/**
 * Phase 2D · Billing landing — the four-package purchase grid.
 *
 * Reads the server-side catalog (`GET /api/billing/packages`) so pricing
 * displayed here is always in lock-step with the server-resolved amount.
 *
 * On Subscribe: POST /api/billing/checkout → window.location.href = url.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Check } from "lucide-react";

import { authService } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";

const FEATURE_LISTS = {
    starter: [
        "Pre-pour parametric geometry",
        "FHP thermal solver · maturity & strength",
        "Project workspace · multi-pour sequencing",
    ],
    pro: [
        "Everything in Starter",
        "Report 2 PDF · Thermal Cracking & Control Plan",
        "Compliance verdict banner across tabs",
        "DXF import · custom 2D pour cross-sections",
        "Monitoring, calibration, predictive STO yield",
    ],
};

const formatPrice = (amount, currency) => {
    const symbol = currency === "usd" ? "$" : currency.toUpperCase() + " ";
    return `${symbol}${Number(amount).toFixed(2)}`;
};

const termSuffix = (days) => (days === 365 ? "/ year" : days === 30 ? "/ month" : `/ ${days} d`);

/* Format a package label as natural English: "Starter · Monthly" → "Starter Monthly" */
const humanLabel = (label) => (label || "").replace(/\s*·\s*/g, " ");

/* Render an ISO timestamp as "Jul 20, 2026" */
const formatExpiry = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    } catch { return ""; }
};

export default function Billing() {
    const navigate = useNavigate();
    const location = useLocation();
    const { account } = useAuth();

    const [packages, setPackages] = useState([]);
    const [me, setMe] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await authService.http.get("/billing/packages");
                setPackages(r.data.packages || []);
            } catch { setErr("Failed to load packages."); }
            if (account) {
                try {
                    const r = await authService.http.get("/billing/me");
                    setMe(r.data);
                } catch { /* not fatal */ }
            }
        })();
    }, [account]);

    const grid = useMemo(() => {
        const map = {};
        packages.forEach((p) => { map[p.id] = p; });
        return [
            { tier: "starter", monthly: map.starter_monthly, annual: map.starter_annual },
            { tier: "pro",     monthly: map.pro_monthly,     annual: map.pro_annual },
        ];
    }, [packages]);

    const subscribe = async (pkgId) => {
        if (!account) {
            navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
            return;
        }
        setBusyId(pkgId); setErr(null);
        try {
            const origin_url = window.location.origin;
            const r = await authService.http.post("/billing/checkout", { package_id: pkgId, origin_url });
            const url = r?.data?.url;
            if (!url) throw new Error("No checkout URL.");
            window.location.href = url;
        } catch (e) {
            setErr(e?.response?.data?.detail?.message || e?.message || "Checkout failed.");
            setBusyId(null);
        }
    };

    return (
        <div data-testid="billing-page" className="min-h-screen bg-[#f6f7f6] text-[#0e1416] py-16 px-6">
            <div className="max-w-[1080px] mx-auto">
                <Link to="/app" className="tc-mono text-[11px] text-[#5a6669] hover:text-[#01696f]">← Back to app</Link>
                <div className="tc-mono text-[10.5px] tracking-[0.18em] uppercase text-[#01696f] mt-6">Billing · USD</div>
                <h1 className="text-[34px] sm:text-[40px] font-medium leading-[1.1] mt-2">Pick a term pass for Concrete Control.</h1>
                <p className="text-[14px] text-[#5a6669] mt-3 max-w-[64ch] leading-[1.6]">
                    Prepaid 30-day or 365-day term passes. No auto-renewal. Subscribe, pour, renew when you need it.
                </p>

                {me?.subscription_until && !me.lapsed && (
                    <div data-testid="billing-me-current"
                         className="mt-6 flex flex-col gap-1.5 px-4 py-3 border border-[#01696f] bg-[#ffffff] max-w-[640px]">
                        <span className="inline-flex items-center gap-2 tc-mono text-[10px] tracking-[0.18em] uppercase text-[#01696f]">
                            CURRENT
                        </span>
                        <span data-testid="billing-me-current-caption" className="tc-mono text-[11.5px] text-[#0e1416]">
                            You&apos;re on {humanLabel(packages.find((p) => p.id === me.current_package_id)?.label) || `tier ${me.tier}`}.
                            Your access is active until {formatExpiry(me.subscription_until)}.
                        </span>
                    </div>
                )}
                {me?.lapsed && (
                    <div data-testid="billing-me-lapsed"
                         className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 border border-[#b3741f] bg-[#fbf4eb] tc-mono text-[11px]">
                        <span className="text-[#b3741f]">LAPSED</span>
                        <span className="text-[#0e1416]">Your previous term has ended · renew below.</span>
                    </div>
                )}
                {err && (
                    <p data-testid="billing-error"
                       className="mt-4 tc-mono text-[11.5px] text-[#a13434] border border-[#a13434] bg-[#fbeaea] px-3 py-2">
                        {err}
                    </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                    {grid.map((row) => (
                        <div key={row.tier} className="border border-[#d8dcdd] bg-[#ffffff] p-6">
                            <div className="flex items-center justify-between">
                                <span className="tc-mono text-[10.5px] tracking-[0.18em] uppercase text-[#01696f]">{row.tier}</span>
                                <span className="tc-mono text-[10px] text-[#5a6669]">30-day · 365-day</span>
                            </div>
                            <h3 className="text-[22px] font-medium mt-1 capitalize">{row.tier}</h3>
                            <ul className="mt-4 flex flex-col gap-2 text-[13px] text-[#0e1416]">
                                {(FEATURE_LISTS[row.tier] || []).map((f, i) => (
                                    <li key={i} className="flex items-start gap-2"><Check size={13} strokeWidth={1.7} className="text-[#01696f] mt-1" />{f}</li>
                                ))}
                            </ul>
                            <div className="grid grid-cols-2 gap-4 mt-6">
                                {[row.monthly, row.annual].map((pkg) => pkg && (
                                    <div key={pkg.id} data-testid={`billing-card-${pkg.id}`}
                                         className="border border-[#d8dcdd] bg-[#f6f7f6] p-4 flex flex-col">
                                        <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">{pkg.label.split(" · ")[1]}</span>
                                        <div className="tc-mono text-[22px] text-[#0e1416] mt-2">
                                            {formatPrice(pkg.amount, pkg.currency)}
                                            <span className="text-[11px] text-[#5a6669] ml-1">{termSuffix(pkg.term_days)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => subscribe(pkg.id)}
                                            disabled={busyId !== null}
                                            data-testid={`billing-subscribe-${pkg.id}`}
                                            className="mt-4 tc-mono text-[11.5px] px-3 py-2 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-50 text-[#ffffff] transition-colors"
                                        >
                                            {busyId === pkg.id ? "Redirecting…" : "Subscribe"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <p data-testid="billing-cancel-anytime" className="tc-mono text-[11px] text-[#5a6669] mt-10 max-w-[78ch] leading-[1.6]">
                    Cancel any time. Your access remains active until the end of your current paid period.
                    After that, your account reverts to the demo tier.
                </p>
                <p className="tc-mono text-[10.5px] text-[#8a9498] mt-3 max-w-[64ch] leading-[1.6]">
                    Pricing is in USD. Payment is processed via Stripe. No auto-renewal — you choose
                    when to extend. Tier upgrades start a fresh term; renewals at the same tier stack
                    onto your remaining time.
                </p>
            </div>
        </div>
    );
}
